import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { ChevronLeft, FileEdit } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useParentTicketStore, type ParentTicketWithMeta } from '@/store/parentTicket'
import type { ParentTicket, TicketUpdate } from '@/types/parentTicket'
import { TicketService } from '@/lib/services/ticket'
import { useParentTicketDetailForm } from '@/hooks/useParentTicketDetailForm'
import { PageHeader } from '@/components/common/PageHeader'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

import CommentThread from '@/components/helpdesk/TicketDetail/CommentThread'
import TicketProperties from '@/components/helpdesk/TicketDetail/TicketProperties'
import StatusSlaCard from '@/components/helpdesk/TicketDetail/StatusSlaCard'
import TicketUpdateModal from '@/components/helpdesk/TicketDetail/TicketUpdateModal'
import CommentComposer from '@/components/helpdesk/TicketDetail/CommentComposer'
import AtAGlanceStrip, { slaTileFromDueAt, type GlanceTile } from '@/components/helpdesk/TicketDetail/overview/AtAGlanceStrip'
import AssignedTeamPanel from '@/components/helpdesk/TicketDetail/overview/AssignedTeamPanel'
import OverviewQuickLinks, { type QuickLinkItem } from '@/components/helpdesk/TicketDetail/overview/OverviewQuickLinks'
import SlaStatusStrip from '@/components/helpdesk/TicketDetail/overview/SlaStatusStrip'
import OverviewSectionCard from '@/components/helpdesk/TicketDetail/overview/OverviewSectionCard'

type Option = { label: string; value: string }

const SUPPORT_TYPE_LABELS: Record<string, string> = {
  l1_basic: 'L1 - Basic troubleshooting',
  l1_remote: 'L1 - 1.5 Remote support',
  l2_network: 'L2 - Network/Advanced support',
  l3_field: 'L3 - Client Support/Field Support',
}

function computeNeedsDetailFetch(ticket: ParentTicket | undefined): boolean {
  if (!ticket) return true
  const m = ticket as ParentTicketWithMeta
  const hasFullRequest = Boolean(m.request)
  const linkedToStore =
    m.store_id != null || (m.store_code != null && String(m.store_code).trim() !== '')
  const hasFullStore = !linkedToStore || Boolean(m.store)
  return !hasFullRequest || !hasFullStore
}

function MetaPair({ label, value }: { label: string; value?: string | null }) {
  const colors = useThemeColors()
  return (
    <Box>
      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" color={colors.text} fontWeight="500">
        {value || '—'}
      </Text>
    </Box>
  )
}

const TAB_OVERVIEW = 0
const TAB_ACTIVITY = 1
const TAB_DETAILS = 2

export default function ParentTicketViewPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const ticket = useParentTicketStore((state) =>
    id ? state.tickets.find((t) => String(t.id) === String(id)) : undefined
  )
  const mergeTicket = useParentTicketStore((s) => s.mergeTicket)
  const meta = ticket as ParentTicketWithMeta | undefined
  const needsDetailFetch = useMemo(() => computeNeedsDetailFetch(ticket), [ticket])
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [statusOptions, setStatusOptions] = useState<Option[]>([])
  const { showSuccess, showError } = useToastNotification()
  const [ticketUpdates, setTicketUpdates] = useState<TicketUpdate[]>([])
  const [editingUpdate, setEditingUpdate] = useState<TicketUpdate | null>(null)
  const [feScheduleRefreshing, setFeScheduleRefreshing] = useState(false)
  const [saveStatusLoading, setSaveStatusLoading] = useState(false)
  const [tabIndex, setTabIndex] = useState(TAB_OVERVIEW)

  const fetchTicketUpdates = useCallback(async () => {
    if (!id) return
    const res = await TicketService.getTicketUpdates(id, 1, 100)
    if (res.success && res.data) {
      const raw = res.data.data ?? res.data
      setTicketUpdates(Array.isArray(raw) ? raw : [])
    }
  }, [id])

  const { form, setField } = useParentTicketDetailForm(ticket)

  const ticketNumberDisplay = (meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number
  const subjectDisplay = (meta as ParentTicketWithMeta & { subject?: string })?.subject

  useEffect(() => {
    if (!id) {
      navigate('/tickets', { replace: true })
    }
  }, [id, navigate])

  useEffect(() => {
    let cancelled = false
    apiClient
      .get<{ data: Array<{ id: number; code: string; label?: string | null }> }>(
        '/service-catalog/parent-ticket-statuses',
        { params: { per_page: 100 } }
      )
      .then((res) => {
        if (cancelled) return
        const items = res.data?.data
        if (Array.isArray(items)) {
          setStatusOptions(
            items.map((s) => ({
              label: s.label || s.code,
              value: s.code,
            }))
          )
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    fetchTicketUpdates()
  }, [fetchTicketUpdates])

  useEffect(() => {
    if (!id) return
    if (!needsDetailFetch) return

    const ac = new AbortController()
    setFetchError(null)
    setFetching(true)

    TicketService.getParentTicket(id, ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        if (res.success && res.data) {
          mergeTicket(res.data)
        } else {
          setFetchError(res.message ?? 'Failed to load ticket')
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted || axios.isCancel(err)) return
        setFetchError('Failed to load ticket')
      })
      .finally(() => {
        setFetching(false)
      })

    return () => {
      ac.abort()
      setFetching(false)
    }
  }, [id, needsDetailFetch, mergeTicket])

  if (!id) {
    return null
  }

  if (fetching) {
    return (
      <Box
        bg={colors.bg}
        p={8}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="240px"
      >
        <Spinner size="lg" thickness="3px" color={colors.primary} />
        <Text ml={3} color={colors.textSecondary} fontWeight="500">
          Loading ticket…
        </Text>
      </Box>
    )
  }

  if (fetchError) {
    return (
      <Box p={{ base: 3, md: 6 }} bg={colors.bg}>
        <Card bg={colors.cardBg} borderColor={colors.border} variant="outline" maxW="lg">
          <CardBody>
            <Text color={colors.text}>{fetchError}</Text>
            <Button variant="link" colorScheme="blue" mt={3} size="sm" onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  if (!ticket) {
    return (
      <Box p={{ base: 3, md: 6 }} bg={colors.bg}>
        <Card bg={colors.cardBg} borderColor={colors.border} variant="outline" maxW="lg">
          <CardBody>
            <Text color={colors.text}>Parent ticket not found.</Text>
            <Button variant="link" colorScheme="blue" mt={3} size="sm" onClick={() => navigate('/tickets')}>
              Back to list
            </Button>
          </CardBody>
        </Card>
      </Box>
    )
  }

  const storeData = meta?.store
  const requestData = meta?.request
  const ticketWithStoreCode = ticket as typeof ticket & { store_code?: string }
  const storeCode = meta?.store_code || ticketWithStoreCode.store_code || storeData?.storeCode

  const ticketPropsForProperties = {
    ticketNumber: (meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number ?? undefined,
    description: ticket.description,
    notes: (meta as ParentTicketWithMeta & { notes?: string })?.notes ?? undefined,
    workPermit:
      (meta as ParentTicketWithMeta & { workPermit?: string; work_permit?: string })?.workPermit ??
      (meta as ParentTicketWithMeta & { work_permit?: string })?.work_permit ??
      undefined,
    workPermitPath:
      (meta as ParentTicketWithMeta & { work_permit_path?: string; workPermitPath?: string })?.work_permit_path ??
      (meta as ParentTicketWithMeta & { workPermitPath?: string })?.workPermitPath ??
      undefined,
    scheduledAt:
      (meta as ParentTicketWithMeta & { scheduledAt?: string })?.scheduledAt ??
      (ticket as ParentTicket & { scheduledAt?: string })?.scheduledAt ??
      undefined,
    assets: ((meta as Record<string, unknown>)?.assets ?? undefined) as
      | { id: number; tag_number?: string | null; model?: string | null }[]
      | undefined,
    source: (meta as ParentTicketWithMeta & { source?: string })?.source ?? undefined,
    severity: ticket.severity,
    priority: ticket.severity,
    supportType: (meta as ParentTicketWithMeta & { support_type?: string })?.support_type ?? undefined,
    subject: (meta as ParentTicketWithMeta & { subject?: string })?.subject ?? undefined,
    status: ticket.status,
  }

  const sharedTicketPropertiesProps = {
    request: requestData,
    ticket: ticketPropsForProperties,
    ticketId: id,
    /** Require resolved store from client DB (not raw store_id alone) so FE recommender uses correct SIMA tenant */
    storeId: storeData?.storeId ?? null,
    storeCode: storeCode ?? undefined,
    clientCode:
      storeData?.clientCode ??
      requestData?.clientCode ??
      (meta as ParentTicketWithMeta & { clientCode?: string })?.clientCode ??
      undefined,
    primaryEngineer: meta?.primaryEngineer,
    backupEngineer: meta?.backupEngineer,
    storeDetails: storeData
      ? {
          businessUnitName: storeData.businessUnitName ?? null,
          storeName: storeData.storeName ?? null,
          branchCode: storeCode ?? storeData.storeCode ?? storeData.branchCode ?? null,
          contactPerson: storeData.contactPerson ?? null,
          email: storeData.storeEmail ?? null,
          contactNumber: storeData.storeContact ?? storeData.contactNumber ?? null,
          storeAddress: storeData.branchAddress ?? storeData.locationAddress ?? null,
        }
      : null,
    onWorkPermitUpload: async (ticketId: string, file: File) => {
      try {
        const fd = new FormData()
        fd.append('_method', 'PUT')
        fd.append('workPermitFile', file)
        await apiClient.post(`/tickets/${ticketId}`, fd)
        const res = await TicketService.getParentTicket(ticketId)
        if (res.success && res.data) mergeTicket(res.data)
        showSuccess('Work permit uploaded')
        fetchTicketUpdates()
      } catch (err) {
        showError(err as Error)
      }
    },
    onSchedulesChange: async () => {
      await fetchTicketUpdates()
      setFeScheduleRefreshing(true)
      try {
        const res = await TicketService.getParentTicket(id!)
        if (res.success && res.data) mergeTicket(res.data)
      } finally {
        setFeScheduleRefreshing(false)
      }
    },
    feScheduleRefreshing,
    onNotesUpdate: async () => {
      if (!id) return
      const res = await TicketService.getParentTicket(id)
      if (res.success && res.data) mergeTicket(res.data)
    },
    onPartsChange: fetchTicketUpdates,
    isReadOnly: false,
    embedded: true,
  }

  const supportTypeRaw = ticketPropsForProperties.supportType
  const assetsCount = ticketPropsForProperties.assets?.length ?? 0
  const partsCount = ticket.ticketParts?.length ?? 0
  const activityCount = ticketUpdates.length
  const feDisplayName = meta?.primaryEngineer?.full_name ?? null
  const detailsTabHint = assetsCount > 0 || partsCount > 0 || Boolean(feDisplayName || meta?.backupEngineer)
  const slaGlance = slaTileFromDueAt(ticket.slaDueAt)

  const glanceTiles: GlanceTile[] = [
    {
      id: 'status',
      label: 'Status',
      value: statusOptions.find((o) => o.value === ticket.status)?.label ?? ticket.status,
      onClick: () => setTabIndex(TAB_OVERVIEW),
    },
    {
      id: 'sla',
      label: 'SLA',
      value: slaGlance.value,
      subValue: slaGlance.subValue,
      tone: slaGlance.tone,
      onClick: () => setTabIndex(TAB_OVERVIEW),
    },
    {
      id: 'fe',
      label: 'Field engineer',
      value: feDisplayName ?? 'Unassigned',
      tone: feDisplayName ? 'blue' : 'default',
      onClick: () => setTabIndex(TAB_DETAILS),
    },
    {
      id: 'parts',
      label: 'Parts',
      value: partsCount > 0 ? `${partsCount} listed` : 'None',
      tone: partsCount > 0 ? 'orange' : 'default',
      onClick: () => setTabIndex(TAB_DETAILS),
    },
  ]

  const quickLinks: QuickLinkItem[] = [
    {
      id: 'activity',
      label: 'View activity & comments',
      count: activityCount,
      onClick: () => setTabIndex(TAB_ACTIVITY),
    },
    ...(assetsCount > 0
      ? [
          {
            id: 'assets',
            label: 'View assets',
            count: assetsCount,
            onClick: () => setTabIndex(TAB_DETAILS),
          } as QuickLinkItem,
        ]
      : []),
    {
      id: 'schedule',
      label: 'FE schedule & parts',
      onClick: () => setTabIndex(TAB_DETAILS),
    },
  ]

  const handleSaveStatus = async () => {
    const prevLabel = statusOptions.find((o) => o.value === ticket.status)?.label || ticket.status
    const newLabel = statusOptions.find((o) => o.value === form.status)?.label || form.status
    setSaveStatusLoading(true)
    try {
      await apiClient.patch(`/tickets/${id}`, { status: form.status })
      mergeTicket({ ...ticket, status: form.status } as ParentTicket)
      showSuccess('Status updated')
      await TicketService.createTicketUpdate(id!, {
        content: `Status changed from "${prevLabel}" to "${newLabel}"`,
        type: 'status_change',
        is_internal: true,
      })
      await fetchTicketUpdates()
    } catch (err) {
      showError(err as Error)
    } finally {
      setSaveStatusLoading(false)
    }
  }

  const overviewActionRail = (
    <VStack align="stretch" spacing={4}>
      <OverviewSectionCard title="Ticket actions">
        <VStack align="stretch" spacing={3}>
          <SlaStatusStrip slaDueAt={ticket.slaDueAt ?? null} />
          <StatusSlaCard
            statusLabel={form.status}
            slaDueAt={ticket.slaDueAt ?? null}
            statusValue={form.status}
            statusOptions={statusOptions}
            totalTicketTime={
              (meta as ParentTicketWithMeta & { total_ticket_time?: string | null })?.total_ticket_time ?? null
            }
            onChangeStatus={(value) => setField('status', value as ParentTicket['status'])}
            onUpdateStatus={handleSaveStatus}
            isSavingStatus={saveStatusLoading}
            isReadOnly={false}
            hidePriority
            hideLiveClock
            hideSectionTitle
            embedded
          />
        </VStack>
      </OverviewSectionCard>
      <AssignedTeamPanel
        primaryEngineer={meta?.primaryEngineer}
        backupEngineer={meta?.backupEngineer}
        onManageSchedule={() => setTabIndex(TAB_DETAILS)}
      />
      <OverviewSectionCard title="Classification">
        <SimpleGrid columns={1} spacing={3}>
          <MetaPair
            label="Support type"
            value={
              supportTypeRaw ? SUPPORT_TYPE_LABELS[supportTypeRaw] ?? supportTypeRaw : undefined
            }
          />
          <MetaPair label="Source" value={ticketPropsForProperties.source} />
          {ticket.agentName ? <MetaPair label="Agent" value={ticket.agentName} /> : null}
        </SimpleGrid>
      </OverviewSectionCard>
      <OverviewQuickLinks links={quickLinks} />
    </VStack>
  )

  return (
    <Box
      bg={colors.bg}
      p={{ base: 3, md: 4 }}
      pb={8}
      w="100%"
      minW={0}
      minH="calc(100vh - 80px - 48px)"
      display="flex"
      flexDir="column"
    >
      <PageHeader
        mb={3}
        start={
          <Button
            variant="ghost"
            leftIcon={<ChevronLeft size={18} />}
            onClick={() => navigate('/tickets')}
            px={2}
            color={colors.text}
            _hover={{ bg: colors.sidebarHoverBg }}
          >
            Back to tickets
          </Button>
        }
      />

      <Card
        variant="outline"
        w="100%"
        bg={colors.cardBg}
        borderColor={colors.border}
        flex="1"
        minW={0}
        shadow="sm"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        <CardBody p={0} display="flex" flexDirection="column" flex="1" minH={0}>
          {/* Hero */}
          <Box
            px={{ base: 4, md: 5 }}
            py={{ base: 4, md: 5 }}
            bg={colors.sectionBg ?? colors.cardBg}
            borderBottom="1px solid"
            borderColor={colors.borderLight}
          >
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              gap={4}
              align={{ base: 'flex-start', lg: 'flex-start' }}
              justify="space-between"
            >
              <Box minW={0} flex="1">
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color={colors.textMuted}
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  mb={1.5}
                >
                  Parent ticket
                </Text>
                <HStack spacing={2} flexWrap="wrap" align="flex-start" mb={1}>
                  <Heading size="md" fontWeight="700" color={colors.heading} noOfLines={3} lineHeight="snug">
                    {subjectDisplay || `Ticket #${ticketNumberDisplay ?? id}`}
                  </Heading>
                  <Badge colorScheme="blue" fontSize="0.7rem" px={2} py={0.5} borderRadius="md" textTransform="none">
                    {ticket.status}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color={colors.textSecondary}>
                  <Text as="span" fontWeight="600" color={colors.text}>
                    {ticketNumberDisplay ?? id}
                  </Text>
                  {ticket.clientName ? ` · ${ticket.clientName}` : null}
                </Text>
              </Box>
              <HStack spacing={2} flexWrap="wrap" justify={{ base: 'flex-start', lg: 'flex-end' }}>
                {ticket.clientOrg ? (
                  <Badge variant="subtle" colorScheme="gray" fontSize="0.65rem" px={2} py={0.5} borderRadius="md">
                    {ticket.clientOrg}
                  </Badge>
                ) : null}
                {storeCode ? (
                  <Badge variant="subtle" colorScheme="purple" fontSize="0.65rem" px={2} py={0.5} borderRadius="md">
                    Store {storeCode}
                  </Badge>
                ) : null}
                <Button
                  size="sm"
                  leftIcon={<FileEdit size={14} />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => navigate(`/tickets/${id}/edit`)}
                >
                  Edit ticket
                </Button>
              </HStack>
            </Flex>
          </Box>

          <Tabs
            index={tabIndex}
            onChange={setTabIndex}
            flex="1"
            display="flex"
            flexDirection="column"
            minH={0}
            colorScheme="blue"
            variant="line"
            isLazy
          >
            <TabList px={{ base: 4, md: 5 }} borderBottomColor={colors.borderLight}>
              <Tab fontSize="sm" fontWeight="600">
                Overview
              </Tab>
              <Tab fontSize="sm" fontWeight="600">
                <HStack spacing={2}>
                  <Text>Activity & Comments</Text>
                  {activityCount > 0 ? (
                    <Badge colorScheme="blue" borderRadius="full" fontSize="0.65rem" px={1.5}>
                      {activityCount}
                    </Badge>
                  ) : null}
                </HStack>
              </Tab>
              <Tab fontSize="sm" fontWeight="600">
                <HStack spacing={2}>
                  <Text>Details</Text>
                  {detailsTabHint ? (
                    <Badge colorScheme="gray" variant="subtle" borderRadius="full" fontSize="0.65rem" px={1.5}>
                      •
                    </Badge>
                  ) : null}
                </HStack>
              </Tab>
            </TabList>

            <TabPanels flex="1" minH={0} overflow="hidden">
              {/* Overview */}
              <TabPanel px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }} overflowY="auto" h="100%">
                <AtAGlanceStrip tiles={glanceTiles} />
                <Grid
                  templateColumns={{ base: '1fr', lg: 'minmax(0, 3fr) minmax(280px, 2fr)' }}
                  gap={{ base: 6, lg: 8 }}
                  alignItems="start"
                >
                  <GridItem minW={0} order={{ base: 2, lg: 1 }}>
                    <TicketProperties {...sharedTicketPropertiesProps} contentMode="overview" />
                  </GridItem>
                  <GridItem
                    minW={0}
                    order={{ base: 1, lg: 2 }}
                    position={{ lg: 'sticky' }}
                    top={{ lg: 4 }}
                    alignSelf="start"
                    zIndex={1}
                  >
                    {overviewActionRail}
                  </GridItem>
                </Grid>
              </TabPanel>

              {/* Activity & Comments */}
              <TabPanel
                px={{ base: 4, md: 5 }}
                py={{ base: 4, md: 5 }}
                display="flex"
                flexDirection="column"
                minH={{ base: '420px', md: '480px' }}
                h="100%"
                overflow="hidden"
              >
                <Box flex="1" minH={0} display="flex" flexDirection="column">
                  <CommentThread
                    ticketId={id}
                    updates={ticketUpdates}
                    onRefresh={fetchTicketUpdates}
                    onEdit={(u) => setEditingUpdate(u)}
                    embedded
                    flex="1"
                    minH={0}
                  />
                  <CommentComposer ticketId={id} onSubmitted={fetchTicketUpdates} />
                </Box>
              </TabPanel>

              {/* Details */}
              <TabPanel px={{ base: 4, md: 5 }} py={{ base: 4, md: 5 }} overflowY="auto" h="100%">
                <TicketProperties {...sharedTicketPropertiesProps} contentMode="details" />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      <TicketUpdateModal
        isOpen={editingUpdate !== null}
        onClose={() => setEditingUpdate(null)}
        ticketId={id!}
        ticketNumber={(meta as ParentTicketWithMeta & { ticket_number?: string })?.ticket_number}
        onSubmitted={fetchTicketUpdates}
        editingUpdate={editingUpdate}
      />
    </Box>
  )
}
